/* /customer/login — Phone + OTP login.
 *
 * 2-step flow:
 *   1. enter phone → POST /request-otp
 *   2. enter OTP   → POST /verify-otp (sets cookie, redirects to ?next= or /dashboard)
 *
 * In DEV mode the backend returns the OTP inline (`dev_otp`) which we render
 * inside a clearly-labelled yellow banner so testers can complete signup
 * without an SMS provider configured.
 */
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Smartphone,
  ShieldCheck,
  Loader2,
  AlertTriangle,
  KeySquare,
  ArrowLeft,
  Sparkles,
} from "lucide-react";
import BrandLogo from "@/components/BrandLogo";
import { useAuth } from "@/auth/AuthContext";

const formatApiError = (detail, fallback) => {
  if (detail == null) return fallback;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
};

const CustomerLogin = () => {
  const { customerStatus, requestOtp, verifyOtp } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const params = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const next = params.get("next") || "/dashboard";

  const [step, setStep] = useState("phone"); // "phone" | "otp"
  const [phone, setPhone] = useState("");
  const [name, setName] = useState("");
  const [code, setCode] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  const [error, setError] = useState("");
  const [infoMsg, setInfoMsg] = useState("");
  const cooldownTimerRef = useRef(null);

  // noindex
  useEffect(() => {
    const tag = document.createElement("meta");
    tag.name = "robots";
    tag.content = "noindex, nofollow";
    document.head.appendChild(tag);
    const prevTitle = document.title;
    document.title = "Customer Login · Mera Naksha";
    return () => {
      document.head.removeChild(tag);
      document.title = prevTitle;
    };
  }, []);

  useEffect(() => {
    if (customerStatus === "authenticated") {
      navigate(next, { replace: true });
    }
  }, [customerStatus, next, navigate]);

  useEffect(() => {
    if (resendCooldown <= 0) return undefined;
    cooldownTimerRef.current = setTimeout(() => setResendCooldown((c) => c - 1), 1000);
    return () => clearTimeout(cooldownTimerRef.current);
  }, [resendCooldown]);

  const normalisedPhone = phone.replace(/\D+/g, "");
  const phoneValid = /^[6-9]\d{9}$/.test(normalisedPhone.replace(/^91/, ""));

  const onRequestOtp = async (e) => {
    e?.preventDefault();
    setError("");
    if (!phoneValid) {
      setError("Please enter a valid 10-digit Indian mobile number.");
      return;
    }
    setSubmitting(true);
    try {
      const data = await requestOtp(normalisedPhone);
      setInfoMsg(data?.message || "OTP sent to your mobile number.");
      setStep("otp");
      setResendCooldown(30);
    } catch (err) {
      setError(formatApiError(err?.response?.data?.detail, "Could not send OTP. Please try again."));
    } finally {
      setSubmitting(false);
    }
  };

  const onVerify = async (e) => {
    e?.preventDefault();
    setError("");
    if (!/^\d{4,8}$/.test(code.trim())) {
      setError("Enter the OTP we just sent.");
      return;
    }
    setSubmitting(true);
    try {
      await verifyOtp(normalisedPhone, code.trim(), name.trim() || undefined);
      navigate(next, { replace: true });
    } catch (err) {
      setError(formatApiError(err?.response?.data?.detail, "Could not verify OTP."));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="min-h-screen bg-navy text-white grid place-items-center px-4 py-10 relative overflow-hidden"
      data-testid="customer-login-page"
    >
      <div className="absolute -top-32 -left-32 h-80 w-80 rounded-full bg-gold/10 blur-3xl pointer-events-none" />
      <div className="absolute -bottom-32 -right-32 h-80 w-80 rounded-full bg-emerald-400/10 blur-3xl pointer-events-none" />
      <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-gold-dark via-gold to-gold-dark" />

      <div className="w-full max-w-md relative">
        <div className="flex flex-col items-center gap-3 mb-7">
          <div className="bg-white rounded-xl px-3 py-2 shadow-md">
            <BrandLogo size="md" asLink={false} withTagline lang="en" testId="customer-login-brand-logo" />
          </div>
          <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-gold/15 border border-gold/30 text-gold text-[11px] font-bold tracking-widest uppercase">
            <Sparkles className="h-3.5 w-3.5" /> Welcome to Mera Naksha
          </div>
        </div>

        <form
          onSubmit={step === "phone" ? onRequestOtp : onVerify}
          className="bg-white text-foreground rounded-3xl shadow-2xl border-2 border-gold/30 overflow-hidden"
          data-testid="customer-login-form"
        >
          <div className="p-6 sm:p-8 space-y-5">
            <div>
              <h1 className="text-xl sm:text-2xl font-extrabold tracking-tight text-navy">
                {step === "phone" ? "Login or Sign up" : "Enter the OTP"}
              </h1>
              <p className="text-[13px] text-muted-foreground mt-1">
                {step === "phone"
                  ? "We'll send a one-time code to your mobile number to verify it's you."
                  : `Code sent to +91 ${normalisedPhone.slice(-10)}. It expires in 5 minutes.`}
              </p>
            </div>

            {error && (
              <div
                className="flex items-start gap-2.5 rounded-xl bg-red-50 border-2 border-red-200 px-3.5 py-2.5 text-[13px] text-red-800 font-semibold"
                data-testid="customer-login-error"
                role="alert"
              >
                <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
                <span>{error}</span>
              </div>
            )}

            {step === "phone" && (
              <>
                <label className="block">
                  <span className="text-[11px] uppercase tracking-[0.18em] text-navy/70 font-bold">
                    Mobile number
                  </span>
                  <div className="mt-1.5 flex items-center gap-2 rounded-xl border-2 border-border bg-white focus-within:border-navy h-11 overflow-hidden">
                    <span className="pl-3 pr-1 text-[14px] font-bold text-navy tabular-nums select-none">+91</span>
                    <input
                      type="tel"
                      inputMode="numeric"
                      required
                      autoComplete="tel-national"
                      autoFocus
                      value={phone}
                      onChange={(e) => setPhone(e.target.value)}
                      placeholder="98765 43210"
                      data-testid="customer-login-phone"
                      maxLength={14}
                      className="flex-1 h-full bg-transparent text-[15px] tabular-nums outline-none placeholder:text-muted-foreground"
                    />
                  </div>
                </label>
                <label className="block">
                  <span className="text-[11px] uppercase tracking-[0.18em] text-navy/70 font-bold">
                    Your name <span className="text-muted-foreground normal-case tracking-normal">(optional)</span>
                  </span>
                  <input
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="e.g. Ramesh Kumar"
                    data-testid="customer-login-name"
                    maxLength={80}
                    className="mt-1.5 w-full h-11 px-3 rounded-xl border-2 border-border bg-white text-[14px] outline-none focus:border-navy"
                  />
                </label>
                <button
                  type="submit"
                  disabled={submitting}
                  data-testid="customer-login-request-otp"
                  className="w-full min-h-[48px] rounded-2xl bg-gold text-navy font-extrabold text-[14.5px] flex items-center justify-center gap-2 hover:bg-gold-dark hover:text-white active:scale-[0.99] transition-all shadow-[0_8px_18px_-8px_rgba(212,162,42,0.6)] disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {submitting ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" /> Sending OTP…
                    </>
                  ) : (
                    <>
                      <Smartphone className="h-4 w-4" /> Send OTP
                    </>
                  )}
                </button>
              </>
            )}

            {step === "otp" && (
              <>
                {infoMsg && (
                  <div
                    className="rounded-xl bg-emerald-50 border-2 border-emerald-300 px-3.5 py-3 text-[12.5px] text-emerald-900 font-semibold"
                    data-testid="customer-login-info-banner"
                  >
                    <div className="flex items-center gap-2">
                      <KeySquare className="h-4 w-4" />
                      <span className="uppercase tracking-wider text-[11px] font-extrabold">
                        Check your SMS
                      </span>
                    </div>
                    <div className="mt-1 leading-snug">
                      {infoMsg}
                    </div>
                  </div>
                )}

                <label className="block">
                  <span className="text-[11px] uppercase tracking-[0.18em] text-navy/70 font-bold">
                    One-time code
                  </span>
                  <input
                    type="text"
                    inputMode="numeric"
                    pattern="\d*"
                    required
                    autoFocus
                    maxLength={8}
                    value={code}
                    onChange={(e) => setCode(e.target.value.replace(/\D+/g, ""))}
                    placeholder="6-digit code"
                    data-testid="customer-login-otp"
                    className="mt-1.5 w-full h-12 px-3 rounded-xl border-2 border-border bg-white text-center text-[18px] tracking-[0.4em] tabular-nums font-extrabold outline-none focus:border-navy"
                  />
                </label>

                <button
                  type="submit"
                  disabled={submitting}
                  data-testid="customer-login-verify-otp"
                  className="w-full min-h-[48px] rounded-2xl bg-navy text-gold font-extrabold text-[14.5px] flex items-center justify-center gap-2 hover:bg-navy-deep active:scale-[0.99] transition-all disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {submitting ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" /> Verifying…
                    </>
                  ) : (
                    <>
                      <ShieldCheck className="h-4 w-4" /> Verify and continue
                    </>
                  )}
                </button>

                <div className="flex items-center justify-between gap-2 text-[12.5px]">
                  <button
                    type="button"
                    onClick={() => {
                      setStep("phone");
                      setCode("");
                      setError("");
                      setDevOtp(null);
                    }}
                    data-testid="customer-login-back-to-phone"
                    className="inline-flex items-center gap-1.5 text-navy hover:text-gold-dark font-bold"
                  >
                    <ArrowLeft className="h-3.5 w-3.5" /> Change number
                  </button>
                  <button
                    type="button"
                    onClick={onRequestOtp}
                    disabled={resendCooldown > 0 || submitting}
                    data-testid="customer-login-resend-otp"
                    className="text-navy font-bold hover:text-gold-dark disabled:text-muted-foreground disabled:cursor-not-allowed"
                  >
                    {resendCooldown > 0 ? `Resend in ${resendCooldown}s` : "Resend code"}
                  </button>
                </div>
              </>
            )}

            <p className="text-[11.5px] text-muted-foreground text-center leading-snug">
              By continuing, you agree to receive purchase + status updates on WhatsApp from{" "}
              <span className="text-navy font-semibold">+91 97176 67115</span>.
            </p>
          </div>

          <div className="px-6 sm:px-8 py-3 bg-navy/[0.04] border-t border-border text-[11px] text-muted-foreground flex items-center justify-between">
            <span>© Team Mera Naksha</span>
            <span className="inline-flex items-center gap-1 font-semibold">
              <ShieldCheck className="h-3 w-3" /> Secure OTP login
            </span>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CustomerLogin;
